import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useAuth } from "./auth";

// Beta feature catalogue. Every flag the app reads must show up
// here so the Settings → Beta features card can list it and the
// graduate-to-production audit is centralised.
//
// To add a new beta:
//   1. Add an entry to BETA_FEATURES
//   2. Wrap the feature's UI / nav / API calls in `useFeatureFlag(slug)`
//   3. Test as the owner with the flag flipped on
//
// To graduate a feature (everyone sees it):
//   1. Delete the `useFeatureFlag` checks at the gate points
//   2. Delete the catalogue entry below (so the Settings card stops
//      showing the now-pointless toggle)
//   3. Optional: clear the flag from every agency's feature_flags
//      with `update agencies set feature_flags = feature_flags - 'slug'`
//      for cleanliness; harmless to leave behind.
//
// To kill a feature: same as graduate, but instead of removing the
// gates you remove the feature itself. The DB schema can be left
// alone (dead but inert) or dropped in a follow-up migration.
export interface BetaFeature {
  slug: string;
  label: string;
  description: string;
}

export const BETA_FEATURES: BetaFeature[] = [
  // iOS native design moved off the agency-wide feature flag and
  // onto a per-device localStorage preference (src/lib/
  // betaDesignStore.ts) so any iOS user can opt in for their own
  // install. The toggle lives in the IOSDesignPanel in Settings,
  // visible to everyone inside the Capacitor app. Catalogue
  // intentionally empty until the next agency-scoped beta lands.
];

// Read a single flag. Returns `true` only when:
//   * The current user is an owner (the beta gate)
//   * AND the flag is set to true in the agency's feature_flags bag
//
// React-Query caches the agency lookup, so multiple flag checks per
// render pay the network cost only once. Owner check is on the
// useAuth store — no async cost.
//
// When a feature graduates, delete this hook's call sites OR change
// the gate here to just `return enabled` (drop the owner check).
export function useFeatureFlag(slug: string): boolean {
  const user = useAuth((s) => s.user);
  const isOwner = user?.level === "owner";
  // The query runs for everyone (officers, supervisors) because the
  // hook is called unconditionally from the components that gate on
  // it. Cheap: same query as the rest of the app uses for the agency
  // row. The `enabled` check below short-circuits non-owners before
  // we even look at the flag, so they always see `false`.
  const agency = useQuery({
    queryKey: ["agency"],
    queryFn: api.getAgency,
    // Don't bother fetching on this hook's behalf if the user can't
    // unlock anything by it. Layout / Settings still trigger the
    // fetch for other reasons, so the cache stays warm.
    enabled: isOwner,
  });
  if (!isOwner) return false;
  return agency.data?.featureFlags?.[slug] === true;
}
